import { useEffect } from 'react';
import useAnalytics from '../../hooks/useAnalytics';

interface AnalyticsTrackerProps {
  children: React.ReactNode;
}

// Define the extended Performance interface with memory property
interface ExtendedPerformance extends Performance {
  memory?: {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
  };
}

/**
 * Component that wraps the application to enable analytics tracking
 * It doesn't render anything visible, just sets up the analytics hooks
 */
const AnalyticsTracker: React.FC<AnalyticsTrackerProps> = ({ children }) => {
  const analytics = useAnalytics();

  // Track initial page load time
  useEffect(() => {
    const loadTime = window.performance.timing.domContentLoadedEventEnd - window.performance.timing.navigationStart;
    
    analytics.logTiming('Performance', 'page_load', loadTime, window.location.pathname);
    
    // Track additional metrics for performance monitoring
    if ('memory' in window.performance) {
      const performance = window.performance as ExtendedPerformance;
      if (performance.memory) {
        analytics.logEvent(
          'Performance', 
          'memory_usage', 
          'JS Heap Size', 
          Math.round(performance.memory.usedJSHeapSize / (1024 * 1024))
        );
      }
    }
    
    // Set up error tracking
    const originalOnError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      analytics.logException(`${message} (${source}:${lineno}:${colno})`, true);
      if (originalOnError) {
        return originalOnError(message, source, lineno, colno, error);
      }
      return false;
    };
    
    return () => {
      window.onerror = originalOnError;
    };
  }, [analytics]);

  return <>{children}</>;
};

export default AnalyticsTracker; 